/**
* @file 品牌定制
* 集团品牌与超管后台「定制后台」维护同一份数据；保存后分账号及其子账号读时向上取集团配置，立即生效。
*/
<template>
  <div v-loading="loading" class="v-brand-custom">
    <plv-tips class="v-brand-custom__notice">
      定制后台用于配置企业专属的登录页、管理后台及频道默认品牌展示
    </plv-tips>

    <plv-form
      ref="form"
      :model="form"
      :rules="rules"
      form-width="570px"
      label-width="150px"
    >
      <div class="v-brand-custom__group-title">管理后台</div>

      <plv-form-item label="品牌开关" prop="customBrandEnabled" tips-inline>
        <div class="v-brand-custom__switch">
          <plv-switch
            v-model="brandEnabled"
            :disabled="loading"
          />
        </div>
        <template #tips>开启后，可对分账号的管理后台进行品牌定制</template>
      </plv-form-item>

      <plv-form-item label="网站域名" prop="customDomain" tips-inline>
        <plv-input-url
          v-model="form.customDomain"
          :protocol-options="PROTOCOL_OPTIONS"
          placeholder="请输入域名，如：live.polyv.net"
          :maxlength="60"
        />
        <template #tips>用于访问定制页面的域名</template>
      </plv-form-item>

      <plv-form-item label="浏览器标签" prop="customWebName" tips-inline>
        <plv-input
          v-model="form.customWebName"
          placeholder="请输入网站名称"
          :maxlength="50"
        />
        <template #tips>展示在浏览器标签页及页面标题中</template>
      </plv-form-item>

      <plv-form-item label="主持人名称" prop="customPublisher">
        <plv-input
          v-model="form.customPublisher"
          placeholder="请输入"
          :maxlength="50"
        />
      </plv-form-item>

      <plv-form-item label="登录页地址" prop="customLoginUrl">
        <plv-input-url
          v-model="form.customLoginUrl"
          :protocol-options="PROTOCOL_OPTIONS"
          placeholder="请输入登录页地址"
          :maxlength="256"
        />
      </plv-form-item>

      <plv-form-item
        v-for="item in imageItems"
        :key="item.type"
        :label="item.label"
        tips-inline
      >
        <plv-image-upload
          v-model="form[item.field]"
          :chuangkit-enabled="false"
          :accept-type="item.acceptType"
          :size-limit="IMAGE_SIZE_LIMIT_KB"
          :upload-action="createUploadAction(item.type)"
          :append-params="{ type: item.type }"
          preview
          :preview-width="item.previewWidth"
          :preview-height="item.previewHeight"
        >
          <plv-button size="small">上传图片</plv-button>
        </plv-image-upload>
        <template #tips>{{ item.tips }}</template>
      </plv-form-item>

      <plv-form-item label="仅显示云直播" prop="hideOtherEntranceEnabled">
        <plv-select v-model="form.hideOtherEntranceEnabled">
          <plv-select-option label="关闭" value="N" />
          <plv-select-option label="开启" value="Y" />
        </plv-select>
      </plv-form-item>

      <plv-form-item label="后台主题色" prop="customThemeColor" tips-inline>
        <div class="v-brand-custom__color">
          <plv-color-picker
            v-model="form.customThemeColor"
            placeholder="请选择主题色"
          />
        </div>
        <template #tips>不选择时使用保利威默认主题色</template>
      </plv-form-item>

      <div class="v-brand-custom__group-title">客户端下载链接</div>

      <plv-form-item label="Windows 下载地址" prop="customClientUrl" tips-inline>
        <plv-input-url
          v-model="form.customClientUrl"
          :protocol-options="PROTOCOL_OPTIONS"
          placeholder="请输入 Windows 客户端下载地址"
          :maxlength="256"
        />
        <template #tips>Windows 地址同时作用于直播助手与云课堂</template>
      </plv-form-item>

      <plv-form-item label="Mac 下载地址" prop="customClientMacUrl" tips-inline>
        <plv-input-url
          v-model="form.customClientMacUrl"
          :protocol-options="PROTOCOL_OPTIONS"
          placeholder="请输入 Mac 客户端下载地址"
          :maxlength="256"
        />
        <template #tips>为空时 Mac 沿用 Windows 下载地址</template>
      </plv-form-item>

      <plv-form-item>
        <plv-button :disabled="loading" @click="onSave">保存</plv-button>
      </plv-form-item>
    </plv-form>
  </div>
</template>

<script>
const IMAGE_ITEMS = [
  {
    type: 'brandLogo',
    field: 'customLogo',
    label: '网站LOGO',
    acceptType: 'image/png',
    previewWidth: '400px',
    previewHeight: '100px',
    tips: '支持 png 格式，建议尺寸为192*24，大小不超过 2MB'
  },
  {
    type: 'brandCover',
    field: 'customCoverImg',
    label: '频道图标',
    acceptType: 'image/png',
    previewWidth: '200px',
    previewHeight: '200px',
    tips: '支持 png 格式，建议尺寸为128*128，大小不超过 2MB'
  },
  {
    type: 'brandIcon',
    field: 'customIcon',
    label: '浏览器图标',
    acceptType: 'image/x-icon,image/vnd.microsoft.icon',
    previewWidth: '200px',
    previewHeight: '200px',
    tips: '支持 ico 格式，建议尺寸为32*32，大小不超过 2MB'
  }
];

// size-limit 单位为 kb
const IMAGE_SIZE_LIMIT_KB = 2 * 1024;

// 各地址输入框的可选协议，plv-input-url 取首项为默认值
const PROTOCOL_OPTIONS = [
  { label: 'https://', value: 'https://' },
  { label: 'http://', value: 'http://' }
];

function createForm() {
  return {
    customBrandEnabled: 'N',
    customDomain: '',
    customWebName: '',
    customPublisher: '',
    customLoginUrl: '',
    customClientUrl: '',
    customClientMacUrl: '',
    customLogo: '',
    customCoverImg: '',
    customIcon: '',
    hideOtherEntranceEnabled: 'N',
    customThemeColor: ''
  };
}

export default {
  data() {
    return {
      form: createForm(),
      imageItems: IMAGE_ITEMS,
      IMAGE_SIZE_LIMIT_KB,
      PROTOCOL_OPTIONS,
      loading: false,
      rules: {}
    };
  },

  computed: {
    brandEnabled: {
      get() {
        return this.form.customBrandEnabled === 'Y';
      },
      set(val) {
        this.form.customBrandEnabled = val ? 'Y' : 'N';
      }
    }
  },

  created() {
    this.getBrand();
  },

  methods: {
    async getBrand() {
      try {
        this.loading = true;
        const data = await this.$store.dispatch('account/getGroupBrand');
        this.form = Object.assign(createForm(), data || {});
      } finally {
        this.loading = false;
      }
    },

    /**
     * 生成 plv-image-upload 的上传回调，Promise 返回图片地址
     * @param {string} type 图片类型，见后端 UploadImageType
     */
    createUploadAction(type) {
      return ({ image }) =>
        this.$store.dispatch('account/uploadImage', { type, image });
    },

    async onSave() {
      try {
        this.loading = true;
        await this.$refs.form.validate();
        await this.$store.dispatch('account/saveGroupBrand', this.form);
        this.$toast.success('保存成功');
        // 保存后回读，页面展示最新内容
        await this.getBrand();
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.v-brand-custom {
  padding: 24px;

  &__notice {
    margin-bottom: 24px;
  }

  // switch 本体只有 20px，比表单行矮：plv-form label 的 min-height 是 40px 并在其中居中，
  // 直接放会比 label 高 10px。撑到同样的行高再居中，label、开关、行尾 tips 才在同一水平线
  &__switch {
    display: flex;
    align-items: center;
    min-height: 40px;
  }

  &__color {
    display: inline-block;
  }

  &__group-title {
    margin: 8px 0 16px;
    padding-left: 8px;
    border-left: 3px solid #3f76fc;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
  }
}
</style>
